import { useCallback, useRef } from 'react';

export type RefreshFetchPolicy = 'store-only' | 'network-only';

/**
 * Refreshes preloaded queries without dropping the boundary that renders them.
 *
 * Loading a query `network-only` suspends whatever renders it, so refreshing after a mutation
 * collapses the surrounding Suspense boundary to its loader. That costs more than a flicker: React
 * detaches the refs inside a boundary while it is in flight, so any imperative call made in that
 * window — dismissing a toast, say — lands on a null ref and is silently dropped. Fetching into the
 * Relay store first and only swapping the refs once the data is there keeps the content mounted.
 *
 * Call `supersede` when the scope those queries are keyed to changes, so a slower refresh cannot
 * swap the previous scope's variables back in behind a newer load.
 */
export function useStoreFirstRefresh() {
  const token = useRef(0);

  const supersede = useCallback(() => {
    token.current += 1;
  }, []);

  const refresh = useCallback((fetches: Promise<unknown>[], swap: (policy: RefreshFetchPolicy) => void) => {
    const mine = token.current;
    const stillOwnsTheView = () => mine === token.current;

    return Promise.all(fetches)
      .then(() => {
        if (stillOwnsTheView()) swap('store-only');
      })
      .catch(() => {
        //fall back to the suspending load rather than leaving the view on data we know is stale
        if (stillOwnsTheView()) swap('network-only');
      });
  }, []);

  return { refresh, supersede };
}
